/* # i18n Configuration
   - Manages English and Greek translations
   - Updated fault reports translations with clearer naming for status updates
*/
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
  en: {
    translation: {
      // Common terms
      common: {
        welcome: 'Welcome',
        loading: 'Loading...',
        save: 'Save',
        cancel: 'Cancel',
        edit: 'Edit',
        delete: 'Delete',
        confirm: 'Confirm',
        search: 'Search',
        filter: 'Filter',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        close: 'Close',
        submit: 'Submit',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        info: 'Info',
        add: 'Add',
        update: 'Update',
        view: 'View',
        download: 'Download',
        upload: 'Upload',
        all: 'All',
        none: 'None',
        required: 'Required',
        optional: 'Optional',
        yes: 'Yes',
        no: 'No',
        select: 'Select',
        remove: 'Remove',
        clear: 'Clear',
        mainContent: 'Main Content',
        skipToContent: 'Skip to content'
      },

      // Header
      header: {
        appName: 'Property Management',
        property: 'Property',
        building: 'Building',
        logout: 'Logout',
        language: 'Language'
      },

      // Sidebar
      sidebar: {
        appTitle: 'ManageHub',
        signedInAs: 'Signed in as',
        dashboard: 'Dashboard',
        faultReports: 'Fault Reports',
        forum: 'Forum',
        notifications: 'Notifications',
        settings: 'Settings',
        profile: 'Profile',
        management: 'Management',
        infrastructure: 'Infrastructure',
        userManagement: 'User Management',
        announcements: 'Announcements',
        analytics: 'Analytics',
        contacts: 'Contacts',
        propertySetup: 'Property Setup'
      },

      // Login
      login: {
        title: 'ManageHub',
        subtitle: 'Secure Portal Access',
        emailLabel: 'Email Address',
        emailPlaceholder: 'name@property.com',
        passwordLabel: 'Password',
        passwordPlaceholder: '••••••••',
        forgotPassword: 'Forgot Password?',
        signInButton: 'Sign In to Dashboard',
        verifying: 'Verifying...',
        authenticating: 'Authenticating...',
        welcomeBack: 'Welcome back!',
        authFailed: 'Authentication failed. Please check your credentials.',
        systemOperational: 'System Operational',
        version: 'Version 1.8.7.9 (Multi)',
        resetPassword: 'Reset Password',
        resetInstructions: 'Enter your email address to receive instructions to reset your password.',
        sendResetLink: 'Send Reset Instructions',
        backToLogin: 'Back to Login',
        resetLinkSent: 'Reset instructions sent!',
        checkEmail: 'If an account exists with this email, you will receive instructions shortly.',
        sendingInstructions: 'Sending instructions...'
      },

      // Dashboard
      dashboard: {
        welcomeBack: 'Welcome back, {{name}}!',
        roleDashboard: '{{role}} Dashboard',
        openReports: 'Open Reports',
        inProgress: 'In Progress',
        completed: 'Completed',
        urgentIssues: 'Urgent Issues',
        latestAnnouncements: 'Latest Announcements',
        noAnnouncements: 'No announcements yet',
        quickActions: 'Quick Actions',
        reportIssue: 'Report Issue',
        manageUsers: 'Manage Users'
      },

      // Profile
      profile: {
        fullName: 'Full Name',
        editProfile: 'Edit Profile',
        saveChanges: 'Save Changes',
        accountDetails: 'Account Details',
        emailAddress: 'Email Address',
        systemRole: 'System Role',
        property: 'Property',
        building: 'Building',
        unitNumber: 'Unit Number',
        unitPlaceholder: 'Unit Number (e.g., 401A)',
        memberSince: 'Member Since',
        accountSettings: 'Account Settings',
        comingSoon: 'Additional settings and preferences coming soon.',
        selectProperty: 'Select Property',
        selectBuilding: 'Select Building',
        changePassword: 'Change Password',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm New Password',
        passwordMismatch: 'Passwords do not match',
        passwordChanged: 'Password updated successfully',
        passwordChangeFailed: 'Failed to update password',
        invalidCurrentPassword: 'Invalid current password',
        updatingPassword: 'Updating password...'
      },

      // Settings
      settings: {
        title: 'Settings',
        subtitle: 'Manage your preferences and account settings',
        roleAndPermissions: 'Your Role & Permissions',
        currentRole: 'Current Role:',
        fullAccessMessage: 'You have full access to all system settings and configurations.',
        limitedAccessMessage: 'You can manage your notification preferences and personal settings.',
        notificationPreferences: 'Notification Preferences',
        mentions: 'Mentions',
        mentionsDesc: 'Notify when someone mentions you',
        propertyTopics: 'Property-wide Forum Topics',
        propertyTopicsDesc: 'Notify on new property-wide forum topics',
        buildingTopics: 'Building Forum Topics',
        buildingTopicsDesc: 'Notify on new topics in your building',
        topicReplies: 'Topic Replies',
        topicRepliesDesc: 'Notify on replies to topics you participated in',
        propertyFaults: 'Property-wide Fault Reports',
        propertyFaultsDesc: 'Notify on new property-wide fault reports',
        buildingFaults: 'Building Forum Topics',
        buildingFaultsDesc: 'Notify on new fault reports in your building',
        faultStatus: 'Fault Status Changes',
        faultStatusDesc: 'Notify on status changes of your fault reports',
        announcementsToggle: 'Announcements',
        announcementsDesc: 'Notify on new announcements',
        emailNotifications: 'Email Notifications',
        emailNotificationsDesc: 'Receive notifications via email (in addition to in-app notifications)',
        savePreferences: 'Save Preferences',
        preferencesSaved: 'Notification preferences saved',
        preferencesFailed: 'Failed to save preferences',
        languagePreference: 'Language Preference',
        languageDesc: 'Choose your preferred language for the application interface',
        languageUpdated: 'Language updated successfully',
        languageFailed: 'Failed to update language',
        currentLanguage: 'Current',
        about: 'About Property Management',
        version: 'Version 1.0.0',
        description: 'A comprehensive property management system for managing buildings, fault reports, and community communication.'
      },

      // Roles
      roles: {
        GeneralUser: 'General User',
        Manager: 'Manager',
        Superadmin: 'Superadmin',
        Administrator: 'Administrator'
      },

      // Fault Reports
      faultReports: {
        title: 'Fault Reports',
        issuesTracked: '{{count}} issues tracked',
        syncing: 'Syncing...',
        filters: 'Filters',
        reportIssue: 'Report Issue',
        loadingReports: 'Loading Reports...',
        allClear: 'All Clear!',
        noIssues: 'No maintenance issues have been reported for this location.',
        createFirst: 'Create First Report',
        newReport: 'New Fault Report',
        submitRequest: 'Submit maintenance request',
        scope: 'Scope',
        propertyWide: 'Property-Wide',
        propertyWideDesc: 'Common areas, grounds, or shared facilities',
        buildingSpecific: 'Building-Specific',
        buildingSpecificDesc: 'Specific issues inside or relating to a building',
        building: 'Building',
        selectBuilding: 'Select building',
        fieldTitle: 'Title',
        titleRequired: 'Title is required',
        whatIsIssue: 'What is the issue?',
        description: 'Description',
        provideDetails: 'Provide more details...',
        attachments: 'Attachments',
        maxFiles: 'Max 5',
        maxFilesCount: 'Maximum {{count}} files allowed',
        uploadFiles: 'Tap to upload images or documents',
        fileTypes: 'PDF, DOC, PNG, JPG up to 10MB',
        uploading: 'Uploading...',
        submitting: 'Submitting...',
        submitReport: 'Submit Report',
        reportSubmitted: 'Fault report submitted successfully',
        submitFailed: 'Failed to submit report. Please try again.',
        backToList: 'Back to List',
        editReport: 'Edit Report',
        modifyDetails: 'Modify issue details',
        reported: 'Reported',
        location: 'Location',
        reporter: 'Reporter',
        issueDescription: 'Issue Description',
        administrativeActions: 'Administrative Actions',
        updateProgress: 'Update Progress',
        updateStatus: 'Update Status',
        confirmProgress: 'Are you sure you want to mark this report as In Progress?',
        deleteReport: 'Delete Report',
        deleting: 'Deleting...',
        deleteConfirm: 'Are you sure you want to delete this report?',
        deleteConfirmTitle: 'Delete Fault Report?',
        deleteConfirmMessage: 'This action cannot be undone. This will permanently delete the fault report and all related data including comments, photos, and attachments.',
        confirmDelete: 'Delete Report',
        reportDeleted: 'Report deleted',
        category: 'Category',
        priority: 'Priority',
        status: 'Status',
        specificLocation: 'Specific Location',
        locationPlaceholder: 'Room number, floor, or area...',
        issueTitle: 'Issue Title',
        currentFiles: 'Current Files',
        addedFiles: 'Added Files',
        addMore: 'Tap to add more files',
        savingChanges: 'Saving Changes...',
        updateReport: 'Update Report',
        reportUpdated: 'Report updated successfully',
        updateFailed: 'Failed to update report',
        statusNew: 'New',
        statusAcknowledged: 'Acknowledged',
        statusInProgress: 'In Progress',
        statusWaiting: 'Waiting',
        statusResolved: 'Resolved',
        statusClosed: 'Closed',
        priorityLow: 'Low',
        priorityMedium: 'Medium',
        priorityHigh: 'High',
        priorityUrgent: 'Urgent',
        categoryElectrical: 'Electrical',
        categoryPlumbing: 'Plumbing',
        categoryElevator: 'Elevator',
        categoryPainting: 'Painting',
        categoryLandscaping: 'Landscaping',
        categorySecurity: 'Security',
        categoryOther: 'Other',
        statusTimeline: 'Status Timeline',
        current: 'Current',
        comments: 'Comments',
        totalComments: '{{count}} Total',
        noConversation: 'No conversation yet',
        addComment: 'Add a comment...',
        typeToMention: 'Type @ to mention',
        postComment: 'Post Comment',
        internalNote: 'Internal Staff Note',
        privateNote: 'Private Note',
        commentPosted: 'Comment posted',
        commentFailed: 'Failed to post comment',
        you: 'You',
        justNow: 'Just now',
        clearAll: 'Clear all',
        allScope: 'All',
        myBuilding: 'My Building',
        sortBy: 'Sort By',
        newest: 'Newest',
        sortByPriority: 'Priority',
        sortByStatus: 'Status',
        loadFailed: 'Failed to load fault reports',
        notFound: 'Fault report not found',
        loadDetailsFailed: 'Failed to load fault details',
        statusUpdated: 'Status updated',
        statusUpdateFailed: 'Failed to update status',
        excelAttachment: 'Excel Attachment',
        document: 'Document',
        attachment: 'Attachment',
        anonymous: 'Anonymous',
        anonymousDesc: 'Your identity will be hidden from other users',
        unknownUser: 'Unknown User'
      },

      // Announcements
      announcements: {
        title: 'Announcements',
        subtitle: 'Property updates',
        newAnnouncement: 'New Announcement',
        noAnnouncements: 'No announcements yet',
        publishAnnouncement: 'Publish Announcement',
        editAnnouncement: 'Edit Announcement',
        announcementTitle: 'Title',
        message: 'Message',
        publish: 'Publish',
        published: 'Announcement published',
        updated: 'Announcement updated',
        deleted: 'Announcement deleted',
        saveFailed: 'Failed to save announcement',
        deleteFailed: 'Failed to delete announcement',
        deleteConfirm: 'Are you sure?',
        posted: 'Posted {{date}}'
      },

      // Forum
      forum: {
        title: 'Forum',
        propertyDesc: 'Discuss topics across the entire property',
        buildingDesc: 'Discuss topics within your building',
        newTopic: 'New Topic',
        createNewTopic: 'Create New Topic',
        myBuilding: 'My Building',
        propertyWide: 'Property-wide',
        noTopics: 'No topics found in this category.',
        createFirst: 'Create the first topic',
        categories: {
          all: 'All Categories',
          general: 'General',
          maintenance: 'Maintenance',
          events: 'Events',
          rules: 'Rules',
          suggestions: 'Suggestions'
        },
        searchPlaceholder: 'Search topics...',
        tagFilterPlaceholder: 'Filter by tag...',
        loadingTopics: 'Loading topics...',
        noTopicsFound: 'No topics found. Be the first to start a discussion!',
        backToForum: 'Back to Forum',
        replies: '{{count}} replies',
        pin: 'Pin',
        unpin: 'Unpin',
        lock: 'Lock',
        unlock: 'Unlock',
        delete: 'Delete',
        topicDeleted: 'Topic deleted successfully',
        topicPinned: 'Topic pinned',
        topicUnpinned: 'Topic unpinned',
        topicLocked: 'Topic locked',
        topicUnlocked: 'Topic unlocked',
        topicLockedMsg: 'This topic is locked',
        deleteConfirm: 'Are you sure you want to delete this topic?',
        loadingTopic: 'Loading topic...',
        topicNotFound: 'Topic not found',
        updateFailed: 'Update failed',
        deleteFailed: 'Delete failed',
        topicScope: 'Topic Scope',
        buildingOnly: 'Building members only',
        propertyOnly: 'All property members',
        topicTitle: 'Topic Title',
        titlePlaceholder: 'What do you want to discuss?',
        category: 'Category',
        tags: 'Tags',
        tagsPlaceholder: 'parking, wifi, rules...',
        messageContent: 'Message Content',
        messagePlaceholder: 'Share your thoughts... Type @ to mention someone',
        postTopic: 'Post Topic',
        creating: 'Creating...',
        validationError: 'Title and body are required',
        createSuccess: 'Topic created successfully',
        createFailed: 'Failed to create topic'
      },

      // User Management
      userManagement: {
        title: 'User Management',
        subtitle: 'Sync profile details and security status',
        addUser: 'Add User',
        loading: 'Loading Management Data...',
        loadFailed: 'Failed to load management data',
        user: 'User',
        status: 'Status',
        role: 'Role',
        affiliation: 'Affiliation',
        actions: 'Actions',
        active: 'Active',
        inactive: 'Inactive',
        protected: 'Protected',
        updateUser: 'Update User',
        addNewUser: 'Add New User',
        fullName: 'Full Name',
        emailAddress: 'Email Address',
        accountStatus: 'Account Status',
        allowLogin: 'Allow user to log in',
        security: 'Password / Security',
        cancelReset: 'Cancel Reset',
        resetPassword: 'Reset Password',
        passwordPlaceholder: 'Enter new password',
        systemRole: 'System Role',
        property: 'Property',
        building: 'Building',
        selectProperty: 'Select Property',
        allBuildings: 'All Buildings',
        saveChanges: 'Save Changes',
        processing: 'Processing...',
        successUpdate: 'User updated and synced',
        successCreate: 'User created and registered',
        passwordRequired: 'Password is required for new accounts',
        deleteConfirm: 'Are you sure you want to delete this user? This action cannot be undone.',
        successDelete: 'User deleted successfully'
      },

      // Property Setup
      propertySetup: {
        title: 'Infrastructure Setup',
        subtitle: 'Define properties and organize buildings within your portfolio.',
        newProperty: 'New Property',
        loading: 'Loading property infrastructure...',
        properties: 'Properties',
        noProperties: 'No properties defined yet.',
        noPropertySelected: 'No Property Selected',
        selectPropertyDesc: 'Select a property from the list on the left to manage its buildings.',
        buildingsIn: 'Buildings in {{name}}',
        addBuilding: 'Add Building',
        noBuildings: 'No buildings registered for this property.',
        clickToAddBuilding: 'Click here to add the first building',
        propertyOverview: 'Property Overview',
        noDescription: 'No additional description provided for this property.',
        editProperty: 'Edit Property',
        addNewProperty: 'Add New Property',
        updatePropertyDesc: 'Update property details in the system.',
        createPropertyDesc: 'Create a new property entity in the system.',
        propertyName: 'Property Name',
        fullAddress: 'Full Address',
        descriptionOptional: 'Description (Optional)',
        editBuilding: 'Edit Building',
        registerBuildingUnder: 'Register a building under',
        buildingNameBlock: 'Building Name / Block',
        buildingCode: 'Unique Building Code',
        buildingCodeDesc: 'This code is used for user registration and tracking.',
        saveChanges: 'Save Changes',
        saving: 'Saving...',
        updateBuilding: 'Update Building',
        updateProperty: 'Update Property',
        createProperty: 'Create Property',
        deletePropertyConfirm: 'Are you sure? This will delete all buildings associated with this property.',
        deleteBuildingConfirm: 'Are you sure you want to delete this building?',
        loadFailed: 'Failed to load property data',
        nameAddressRequired: 'Name and Address are required',
        nameCodeRequired: 'Building name and code are required',
        propertyUpdated: 'Property updated successfully',
        propertyCreated: 'Property created successfully',
        buildingUpdated: 'Building updated successfully',
        buildingAdded: 'Building added successfully',
        propertyDeleted: 'Property deleted',
        buildingDeleted: 'Building deleted',
        deleteFailed: 'Delete failed'
      },

      // Notifications
      notifications: {
        title: 'Notifications',
        unreadCount: '{{count}} unread',
        markAllRead: 'Mark All Read',
        all: 'All',
        unread: 'Unread',
        loading: 'Loading notifications...',
        noUnread: 'No unread notifications',
        noNotifications: 'No notifications yet',
        loadFailed: 'Failed to load notifications',
        markReadSuccess: 'All notifications marked as read',
        markReadFailed: 'Failed to mark notifications as read',
        processFailed: 'Failed to process notification'
      },

      // Email Content
      email: {
        viewDetails: 'View Details',
        footer: 'You\'re receiving this because email notifications are enabled in your ManageHub settings.',
        automatedMessage: 'This is an automated message from the ManageHub Property Management System.',
        signIn: 'Sign In to ManageHub',
        passwordReset: {
          title: 'Password Reset Request',
          greeting: 'Hello {{name}},',
          body: 'We received a request to reset your password for your ManageHub account. If you didn\'t make this request, you can safely ignore this email.',
          securityNote: 'Security Note:',
          securityBody: 'For your security, please contact your property administrator or manager directly to receive your temporary password or to have your credentials reset manually.',
          instruction: 'Once you have your new credentials, you can sign in at:'
        },
        invitation: {
          subject: 'Welcome to ManageHub - Your Account Details',
          greeting: 'Hello {{name}},',
          body: 'An account has been created for you on the ManageHub Property Management System.',
          credentialsTitle: 'Your Login Credentials:',
          emailLabel: 'Email:',
          passwordLabel: 'Temporary Password:',
          urgencyNote: 'For security reasons, we strongly recommend that you change your password immediately after your first sign-in.',
          actionButton: 'Sign In to Your Account',
          securityTip: 'If you did not expect this email, please contact your property manager.'
        },
        types: {
          mention: {
            title: '{{name}} mentioned you',
            message: 'You were mentioned in "{{title}}"',
            preview: 'You were mentioned: "{{preview}}"'
          },
          faultStatus: {
            title: 'Fault Status Updated',
            message: 'Your report "{{title}}" changed from {{oldStatus}} to {{newStatus}}'
          },
          faultComment: {
            title: 'New Comment on Your Fault Report',
            message: '{{name}} commented on "{{title}}"'
          },
          faultNew: {
            title: 'New Fault Reported',
            message: '{{name}} reported: {{title}}'
          },
          topicReply: {
            title: 'New Reply to Your Topic',
            message: '{{name}} replied to "{{title}}"'
          },
          announcement: {
            title: 'New Announcement',
            message: '{{title}}'
          }
        }
      }
    }
  },
  el: {
    translation: {
      // Common terms
      common: {
        welcome: 'Καλώς ήρθατε',
        loading: 'Φόρτωση...',
        save: 'Αποθήκευση',
        cancel: 'Ακύρωση',
        edit: 'Επεξεργασία',
        delete: 'Διαγραφή',
        confirm: 'Επιβεβαίωση',
        search: 'Αναζήτηση',
        filter: 'Φίλτρο',
        back: 'Πίσω',
        next: 'Επόμενο',
        previous: 'Προηγούμενο',
        close: 'Κλείσιμο',
        submit: 'Υποβολή',
        success: 'Επιτυχία',
        error: 'Σφάλμα',
        warning: 'Προειδοποίηση',
        info: 'Πληροφορία',
        add: 'Προσθήκη',
        update: 'Ενημέρωση',
        view: 'Προβολή',
        download: 'Λήψη',
        upload: 'Μεταφόρτωση',
        all: 'Όλα',
        none: 'Κανένα',
        required: 'Απαιτείται',
        optional: 'Προαιρετικό',
        yes: 'Ναι',
        no: 'Όχι',
        select: 'Επιλογή',
        remove: 'Αφαίρεση',
        clear: 'Εκκαθάριση',
        mainContent: 'Κύριο Περιεχόμενο',
        skipToContent: 'Μετάβαση στο περιεχόμενο'
      },

      // Header
      header: {
        appName: 'Διαχείριση Ακινήτων',
        property: 'Ακίνητο',
        building: 'Κτίριο',
        logout: 'Αποσύνδεση',
        language: 'Γλώσσα'
      },

      // Sidebar
      sidebar: {
        appTitle: 'ManageHub',
        signedInAs: 'Συνδεδεμένος ως',
        dashboard: 'Πίνακας Ελέγχου',
        faultReports: 'Αναφορές Βλαβών',
        forum: 'Φόρουμ',
        notifications: 'Ειδοποιήσεις',
        settings: 'Ρυθμίσεις',
        profile: 'Προφίλ',
        management: 'Διαχείριση',
        infrastructure: 'Υποδομή',
        userManagement: 'Διαχείριση Χρηστών',
        announcements: 'Ανακοινώσεις',
        analytics: 'Αναλυτικά Στοιχεία',
        contacts: 'Επαφές',
        propertySetup: 'Ρύθμιση Ακινήτου'
      },

      // Login
      login: {
        title: 'ManageHub',
        subtitle: 'Ασφαλής Πρόσβαση',
        emailLabel: 'Διεύθυνση Email',
        emailPlaceholder: 'onoma@akinito.com',
        passwordLabel: 'Κωδικός Πρόσβασης',
        passwordPlaceholder: '••••••••',
        forgotPassword: 'Ξεχάσατε τον κωδικό;',
        signInButton: 'Σύνδεση στον Πίνακα Ελέγχου',
        verifying: 'Επαλήθευση...',
        authenticating: 'Έλεγχος ταυτότητας...',
        welcomeBack: 'Καλώς ήρθατε πίσω!',
        authFailed: 'Η έλεγχος ταυτότητας απέτυχε. Ελέγξτε τα στοιχεία σας.',
        systemOperational: 'Σύστημα σε Λειτουργία',
        version: 'Έκδοση 1.8.7.9 (Multi)',
        resetPassword: 'Επαναφορά Κωδικού',
        resetInstructions: 'Εισαγάγετε τη διεύθυνση email σας για να λάβετε οδηγίες επαναφοράς του κωδικού σας.',
        sendResetLink: 'Αποστολή Οδηγιών Επαναφοράς',
        backToLogin: 'Επιστροφή στη Σύνδεση',
        resetLinkSent: 'Οι οδηγίες εστάλησαν!',
        checkEmail: 'Εάν υπάρχει λογαριασμός με αυτό το email, θα λάβετε οδηγίες σύντομα.',
        sendingInstructions: 'Αποστολή οδηγιών...'
      },

      // Dashboard
      dashboard: {
        welcomeBack: 'Καλώς ήρθατε πίσω, {{name}}!',
        roleDashboard: 'Πίνακας Ελέγχου {{role}}',
        openReports: 'Ανοιχτές Αναφορές',
        inProgress: 'Σε Εξέλιξη',
        completed: 'Ολοκληρωμένες',
        urgentIssues: 'Επείγοντα Θέματα',
        latestAnnouncements: 'Πρόσφατες Ανακοινώσεις',
        noAnnouncements: 'Δεν υπάρχουν ανακοινώσεις ακόμη',
        quickActions: 'Γρήγορες Ενέργειες',
        reportIssue: 'Αναφορά Προβλήματος',
        manageUsers: 'Διαχείριση Χρηστών'
      },

      // Profile
      profile: {
        fullName: 'Ονοματεπώνυμο',
        editProfile: 'Επεξεργασία Προφίλ',
        saveChanges: 'Αποθήκευση Αλλαγών',
        accountDetails: 'Στοιχεία Λογαριασμού',
        emailAddress: 'Διεύθυνση Email',
        systemRole: 'Ρόλος Συστήματος',
        property: 'Ακίνητο',
        building: 'Κτίριο',
        unitNumber: 'Αριθμός Διαμερίσματος',
        unitPlaceholder: 'Αριθμός Διαμερίσματος (π.χ. 401Α)',
        memberSince: 'Μέλος Από',
        accountSettings: 'Ρυθμίσεις Λογαριασμού',
        comingSoon: 'Πρόσθετες ρυθμίσεις και προτιμήσεις θα είναι σύντομα διαθέσιμες.',
        selectProperty: 'Επιλογή Ακινήτου',
        selectBuilding: 'Επιλογή Κτιρίου',
        changePassword: 'Αλλαγή Κωδικού Πρόσβασης',
        currentPassword: 'Τρέχων Κωδικός',
        newPassword: 'Νέος Κωδικός',
        confirmPassword: 'Επιβεβαίωση Νέου Κωδικού',
        passwordMismatch: 'Οι κωδικοί δεν ταιριάζουν',
        passwordChanged: 'Ο κωδικός ενημερώθηκε επιτυχώς',
        passwordChangeFailed: 'Αποτυχία ενημέρωσης κωδικός',
        invalidCurrentPassword: 'Μη έγκυρος τρέχων κωδικός',
        updatingPassword: 'Ενημέρωση κωδικού...'
      },

      // Settings
      settings: {
        title: 'Ρυθμίσεις',
        subtitle: 'Διαχειριστείτε τις προτιμήσεις και τις ρυθμίσεις του λογαριασμού σας',
        roleAndPermissions: 'Ο Ρόλος και τα Δικαιώματά σας',
        currentRole: 'Τρέχων Ρόλος:',
        fullAccessMessage: 'Έχετε πλήρη πρόσβαση σε όλες τις ρυθμίσεις και διαμορφώσεις του συστήματος.',
        limitedAccessMessage: 'Μπορείτε να διαχειριστείτε τις προτιμήσεις ειδοποιήσεων και τις προσωπικές σας ρυθμίσεις.',
        notificationPreferences: 'Προτιμήσεις Ειδοποιήσεων',
        mentions: 'Αναφορές',
        mentionsDesc: 'Ειδοποίηση όταν κάποιος σας αναφέρει',
        propertyTopics: 'Θέματα Φόρουμ σε όλο το Ακίνητο',
        propertyTopicsDesc: 'Ειδοποίηση για νέα θέματα φόρουμ σε όλο το ακίνητο',
        buildingTopics: 'Θέματα Φόρουμ Κτιρίου',
        buildingTopicsDesc: 'Ειδοποίηση για νέα θέματα στο κτίριό σας',
        topicReplies: 'Απαντήσεις σε Θέματα',
        topicRepliesDesc: 'Ειδοποίηση για απαντήσεις σε θέματα που συμμετείχατε',
        propertyFaults: 'Αναφορές Βλαβών σε όλο το Ακίνητο',
        propertyFaultsDesc: 'Ειδοποίηση για νέες αναφορές βλαβών σε όλο το ακίνητο',
        buildingFaults: 'Αναφορές Βλαβών Κτιρίου',
        buildingFaultsDesc: 'Ειδοποίηση για νέες αναφορές βλαβών στο κτίριό σας',
        faultStatus: 'Αλλαγές Κατάστασης Βλάβης',
        faultStatusDesc: 'Ειδοποίηση για αλλαγές κατάστασης στις αναφορές βλαβών σας',
        announcementsToggle: 'Ανακοινώσεις',
        announcementsDesc: 'Ειδοποίηση για νέες ανακοινώσεις',
        emailNotifications: 'Ειδοποιήσεις μέσω Email',
        emailNotificationsDesc: 'Λήψη ειδοποιήσεων μέσω email (επιπλέον των ειδοποιήσεων στην εφαρμογή)',
        savePreferences: 'Αποθήκευση Προτιμήσεων',
        preferencesSaved: 'Οι προτιμήσεις ειδοποιήσεων αποθηκεύτηκαν',
        preferencesFailed: 'Αποτυχία αποθήκευσης προτιμήσεων',
        languagePreference: 'Προτίμηση Γλώσσας',
        languageDesc: 'Επιλέξτε την προτιμώμενη γλώσσα για τη διεπαφή της εφαρμογής',
        languageUpdated: 'Η γλώσσα ενημερώθηκε επιτυχώς',
        languageFailed: 'Αποτυχία ενημέρωσης γλώσσας',
        currentLanguage: 'Τρέχουσα',
        about: 'Σχετικά με τη Διαχείριση Ακινήτων',
        version: 'Έκδοση 1.0.0',
        description: 'Ένα ολοκληρωμένο σύστημα διαχείρισης ακινήτων για τη διαχείριση κτιρίων, αναφορών βλαβών και επικοινωνίας της κοινότητας.'
      },

      // Roles
      roles: {
        GeneralUser: 'Γενικός Χρήστης',
        Manager: 'Διαχειριστής',
        Superadmin: 'Υπερδιαχειριστής',
        Administrator: 'Διαχειριστής Συστήματος'
      },

      // Fault Reports
      faultReports: {
        title: 'Αναφορές Βλαβών',
        issuesTracked: '{{count}} θέματα καταγεγραμμένα',
        syncing: 'Συγχρονισμός...',
        filters: 'Φίλτρα',
        reportIssue: 'Αναφορά Προβλήματος',
        loadingReports: 'Φόρτωση Αναφορών...',
        allClear: 'Όλα Εντάξει!',
        noIssues: 'Δεν έχουν αναφερθεί προβλήματα συντήρησης για αυτήν την τοποθεσία.',
        createFirst: 'Δημιουργία Πρώτης Αναφοράς',
        newReport: 'Νέα Αναφορά Βλάβης',
        submitRequest: 'Υποβολή αιτήματος συντήρησης',
        scope: 'Εμβέλεια',
        propertyWide: 'Σε Όλο το Ακίνητο',
        propertyWideDesc: 'Κοινόχρηστοι χώροι, κήποι ή κοινές εγκαταστάσεις',
        buildingSpecific: 'Συγκεκριμένο Κτίριο',
        buildingSpecificDesc: 'Συγκεκριμένα προβλήματα εντός ή σχετικά με ένα κτίριο',
        building: 'Κτίριο',
        selectBuilding: 'Επιλογή κτιρίου',
        fieldTitle: 'Τίτλος',
        titleRequired: 'Ο τίτλος είναι απαραίτητος',
        whatIsIssue: 'Ποιο είναι το πρόβλημα;',
        description: 'Περιγραφή',
        provideDetails: 'Παρέχετε περισσότερες λεπτομέρειες...',
        attachments: 'Συνημμένα',
        maxFiles: 'Μέγιστο 5',
        maxFilesCount: 'Επιτρέπονται το πολύ {{count}} αρχεία',
        uploadFiles: 'Πατήστε για μεταφόρτωση εικόνων ή εγγράφων',
        fileTypes: 'PDF, DOC, PNG, JPG έως 10MB',
        uploading: 'Μεταφόρτωση...',
        submitting: 'Υποβολή...',
        submitReport: 'Υποβολή Αναφοράς',
        reportSubmitted: 'Η αναφορά βλάβης υποβλήθηκε επιτυχώς',
        submitFailed: 'Αποτυχία υποβολής αναφοράς. Παρακαλώ προσπαθήστε ξανά.',
        backToList: 'Πίσω στη Λίστα',
        editReport: 'Επεξεργασία Αναφοράς',
        modifyDetails: 'Τροποποίηση λεπτομερειών προβλήματος',
        reported: 'Αναφέρθηκε',
        location: 'Τοποθεσία',
        reporter: 'Αναφέρων',
        issueDescription: 'Περιγραφή Προβλήματος',
        administrativeActions: 'Διοικητικές Ενέργειες',
        updateProgress: 'Ενημέρωση Προόδου',
        updateStatus: 'Ενημέρωση Κατάστασης',
        confirmProgress: 'Είστε βέβαιοι ότι θέλετε να σημειώσετε αυτήν την αναφορά ως Σε Εξέλιξη;',
        deleteReport: 'Διαγραφή Αναφοράς',
        deleting: 'Διαγραφή...',
        deleteConfirm: 'Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτήν την αναφορά;',
        deleteConfirmTitle: 'Διαγραφή Αναφοράς Βλάβης;',
        deleteConfirmMessage: 'Αυτή η ενέργεια δεν μπορεί να αναιρεθεί. Αυτό θα διαγράψει οριστικά την αναφορά βλάβης και όλα τα σχετικά δεδομένα, συμπεριλαμβανομένων σχολίων, φωτογραφιών και συνημμένων.',
        confirmDelete: 'Διαγραφή Αναφοράς',
        reportDeleted: 'Η αναφορά διαγράφηκε',
        category: 'Κατηγορία',
        priority: 'Προτεραιότητα',
        status: 'Κατάσταση',
        specificLocation: 'Συγκεκριμένη Τοποθεσία',
        locationPlaceholder: 'Αριθμός δωματίου, όροφος ή περιοχή...',
        issueTitle: 'Τίτλος Προβλήματος',
        currentFiles: 'Τρέχοντα Αρχεία',
        addedFiles: 'Προστεθέντα Αρχεία',
        addMore: 'Πατήστε για προσθήκη περισσότερων αρχείων',
        savingChanges: 'Αποθήκευση Αλλαγών...',
        updateReport: 'Ενημέρωση Αναφοράς',
        reportUpdated: 'Η αναφορά ενημερώθηκε επιτυχώς',
        updateFailed: 'Αποτυχία ενημέρωσης αναφοράς',
        statusNew: 'Νέα',
        statusAcknowledged: 'Αναγνωρίστηκε',
        statusInProgress: 'Σε Εξέλιξη',
        statusWaiting: 'Αναμονή',
        statusResolved: 'Επιλύθηκε',
        statusClosed: 'Κλειστή',
        priorityLow: 'Χαμηλή',
        priorityMedium: 'Μέτρια',
        priorityHigh: 'Υψηλή',
        priorityUrgent: 'Επείγουσα',
        categoryElectrical: 'Ηλεκτρολογικά',
        categoryPlumbing: 'Υδραυλικά',
        categoryElevator: 'Ανελκυστήρας',
        categoryPainting: 'Βαψίματα',
        categoryLandscaping: 'Εξωτερικοί Χώροι',
        categorySecurity: 'Ασφάλεια',
        categoryOther: 'Άλλο',
        statusTimeline: 'Χρονολόιο Κατάστασης',
        current: 'Τρέχον',
        comments: 'Σχόλια',
        totalComments: '{{count}} Σύνολο',
        noConversation: 'Δεν υπάρχει συζήτηση ακόμη',
        addComment: 'Προσθήκη σχολίου...',
        typeToMention: 'Πληκτρολογήστε @ για αναφορά',
        postComment: 'Ανάρτηση Σχολίου',
        internalNote: 'Εσωτερική Σημείωση Προσωπικού',
        privateNote: 'Ιδιωτική Σημείωση',
        commentPosted: 'Το σχόλιο αναρτήθηκε',
        commentFailed: 'Αποτυχία ανάρτησης σχολίου',
        you: 'Εσείς',
        justNow: 'Μόλις τώρα',
        clearAll: 'Εκκαθάριση όλων',
        allScope: 'Όλα',
        myBuilding: 'Το Κτίριό Μου',
        sortBy: 'Ταξινόμηση Κατά',
        newest: 'Νεότερα',
        sortByPriority: 'Προτεραιότητα',
        sortByStatus: 'Κατάσταση',
        loadFailed: 'Αποτυχία φόρτωσης αναφορών βλαβών',
        notFound: 'Η αναφορά βλάβης δεν βρέθηκε',
        loadDetailsFailed: 'Αποτυχία φόρτωσης λεπτομερειών βλάβης',
        statusUpdated: 'Η κατάσταση ενημερώθηκε',
        statusUpdateFailed: 'Αποτυχία ενημέρωσης κατάστασης',
        excelAttachment: 'Συνημμένο Excel',
        document: 'Έγγραφο',
        attachment: 'Συνημμένο',
        anonymous: 'Ανώνυμος',
        anonymousDesc: 'Η ταυτότητά σας θα είναι κρυφή από τους άλλους χρήστες',
        unknownUser: 'Άγνωστος Χρήστης'
      },

      // Announcements
      announcements: {
        title: 'Ανακοινώσεις',
        subtitle: 'Ενημερώσεις ακινήτου',
        newAnnouncement: 'Νέα Ανακοίνωση',
        noAnnouncements: 'Δεν υπάρχουν ανακοινώσεις ακόμη',
        publishAnnouncement: 'Δημοσίευση Ανακοίνωσης',
        editAnnouncement: 'Επεξεργασία Ανακοίνωσης',
        announcementTitle: 'Τίτλος',
        message: 'Μήνυμα',
        publish: 'Δημοσίευση',
        published: 'Η ανακοίνωση δημοσιεύτηκε',
        updated: 'Η ανακοίνωση ενημερώθηκε',
        deleted: 'Η ανακοίνωση διαγράφηκε',
        saveFailed: 'Αποτυχία αποθήκευσης ανακοινώσεις',
        deleteFailed: 'Αποτυχία διαγραφής ανακοίνωσης',
        deleteConfirm: 'Είστε βέβαιοι;',
        posted: 'Δημοσιεύτηκε {{date}}'
      },

      // Forum
      forum: {
        title: 'Φόρουμ',
        propertyDesc: 'Συζητήστε θέματα για όλο το ακίνητο',
        buildingDesc: 'Συζητήστε θέματα εντός του κτιρίου σας',
        newTopic: 'Νέο Θέμα',
        createNewTopic: 'Δημιουργία Νέου Θέματος',
        myBuilding: 'Το Κτίριό Μου',
        propertyWide: 'Σε όλο το Ακίνητο',
        noTopics: 'Δεν βρέθηκαν θέματα σε αυτήν την κατηγορία.',
        createFirst: 'Δημιουργήστε το πρώτο θέμα',
        categories: {
          all: 'Όλες οι Κατηγορίες',
          general: 'Γενικά',
          maintenance: 'Συντήρηση',
          events: 'Εκδηλώσεις',
          rules: 'Κανόνες',
          suggestions: 'Προτάσεις'
        },
        searchPlaceholder: 'Αναζήτηση θεμάτων...',
        tagFilterPlaceholder: 'Φίλτρο κατά ετικέτα...',
        loadingTopics: 'Φόρτωση θεμάτων...',
        noTopicsFound: 'Δεν βρέθηκαν θέματα. Ξεκινήστε εσείς τη συζήτηση!',
        backToForum: 'Πίσω στο Φόρουμ',
        replies: '{{count}} απαντήσεις',
        pin: 'Καρφίτσωμα',
        unpin: 'Ξεκαρφίτσωμα',
        lock: 'Κλείδωμα',
        unlock: 'Ξεκλείδωμα',
        delete: 'Διαγραφή',
        topicDeleted: 'Το θέμα διαγράφηκε επιτυχώς',
        topicPinned: 'Το θέμα καρφιτσώθηκε',
        topicUnpinned: 'Το θέμα ξεκαρφιτσώθηκε',
        topicLocked: 'Το θέμα κλειδώθηκε',
        topicUnlocked: 'Το θέμα ξεκλείδωσε',
        topicLockedMsg: 'Αυτό το θέμα είναι κλειδωμένο',
        deleteConfirm: 'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το θέμα;',
        loadingTopic: 'Φόρτωση θέματος...',
        topicNotFound: 'Το θέμα δεν βρέθηκε',
        updateFailed: 'Η ενημέρωση απέτυχε',
        deleteFailed: 'Η διαγραφή απέτυχε',
        topicScope: 'Εμβέλεια Θέματος',
        buildingOnly: 'Μόνο για μέλη του κτιρίου',
        propertyOnly: 'Όλα τα μέλη του ακινήτου',
        topicTitle: 'Τίτλος Θέματος',
        titlePlaceholder: 'Τι θέλετε να συζητήσετε;',
        category: 'Κατηγορία',
        tags: 'Ετικέτες',
        tagsPlaceholder: 'parking, wifi, rules...',
        messageContent: 'Περιεχόμενο Μηνύματος',
        messagePlaceholder: 'Μοιραστείτε τις σκέψεις σας... Πληκτρολογήστε @ για αναφορά',
        postTopic: 'Ανάρτηση Θέματος',
        creating: 'Δημιουργία...',
        validationError: 'Ο τίτλος και το περιεχόμενο είναι υποχρεωτικά',
        createSuccess: 'Το θέμα δημιουργήθηκε επιτυχώς',
        createFailed: 'Αποτυχία δημιουργίας θέματος'
      },

      // User Management
      userManagement: {
        title: 'Διαχείριση Χρηστών',
        subtitle: 'Συγχρονισμός στοιχείων προφίλ και κατάστασης ασφαλείας',
        addUser: 'Προσθήκη Χρήστη',
        loading: 'Φόρτωση Δεδομένων Διαχείρισης...',
        loadFailed: 'Αποτυχία φόρτωσης δεδομένων διαχείρισης',
        user: 'Χρήστης',
        status: 'Κατάσταση',
        role: 'Ρόλος',
        affiliation: 'Σύνδεση',
        actions: 'Ενέργειες',
        active: 'Ενεργός',
        inactive: 'Ανενεργός',
        protected: 'Προστατευμένος',
        updateUser: 'Ενημέρωση Χρήστη',
        addNewUser: 'Προσθήκη Νέου Χρήστη',
        fullName: 'Ονοματεπώνυμο',
        emailAddress: 'Διεύθυνση Email',
        accountStatus: 'Κατάσταση Λογαριασμού',
        allowLogin: 'Επιτρέψτε στον χρήστη να συνδεθεί',
        security: 'Κωδικός / Ασφάλεια',
        cancelReset: 'Ακύρωση Επαναφοράς',
        resetPassword: 'Επαναφορά Κωδικού',
        passwordPlaceholder: 'Εισαγάγετε νέο κωδικό',
        systemRole: 'Ρόλος Συστήματος',
        property: 'Ακίνητο',
        building: 'Κτίριο',
        selectProperty: 'Επιλογή Ακινήτου',
        allBuildings: 'Όλα τα Κτίρια',
        saveChanges: 'Αποθήκευση Αλλαγών',
        processing: 'Επεξεργασία...',
        successUpdate: 'Ο χρήστης ενημερώθηκε και συγχρονίστηκε',
        successCreate: 'Ο χρήστης δημιουργήθηκε και εγγράφηκε',
        passwordRequired: 'Απαιτείται κωδικός πρόσβασης για νέους λογαριασμούς',
        deleteConfirm: 'Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτόν τον χρήστη; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
        successDelete: 'Ο χρήστης διαγράφηκε επιτυχώς'
      },

      // Property Setup
      propertySetup: {
        title: 'Ρύθμιση Υποδομής',
        subtitle: 'Ορίστε ακίνητα και οργανώστε κτίρια στο χαρτοφυλάκιό σας.',
        newProperty: 'Νέο Ακίνητο',
        loading: 'Φόρτωση υποδομής ακινήτων...',
        properties: 'Ακίνητα',
        noProperties: 'Δεν έχουν οριστεί ακόμη ακίνητα.',
        noPropertySelected: 'Δεν έχει επιλεγεί ακίνητο',
        selectPropertyDesc: 'Επιλέξτε ένα ακίνητο από τη λίστα στα αριστερά για να διαχειριστείτε τα κτίριά του.',
        buildingsIn: 'Κτίρια στο {{name}}',
        addBuilding: 'Προσθήκη Κτιρίου',
        noBuildings: 'Δεν έχουν καταχωρηθεί κτίρια για αυτό το ακίνητο.',
        clickToAddBuilding: 'Κάντε κλικ εδώ για να προσθέσετε το πρώτο κτίριο',
        propertyOverview: 'Επισκόπηση Ακινήτου',
        noDescription: 'Δεν παρέχεται πρόσθετη περιγραφή για αυτό το ακίνητο.',
        editProperty: 'Επεξεργασία Ακινήτου',
        addNewProperty: 'Προσθήκη Νέου Ακινήτου',
        updatePropertyDesc: 'Ενημερώστε τις λεπτομέρειες του ακινήτου στο σύστημα.',
        createPropertyDesc: 'Δημιουργήστε μια νέα οντότητα ακινήτου στο σύστημα.',
        propertyName: 'Όνομα Ακινήτου',
        fullAddress: 'Πλήρης Διεύθυνση',
        descriptionOptional: 'Περιγραφή (Προαιρετικά)',
        editBuilding: 'Επεξεργασία Κτιρίου',
        registerBuildingUnder: 'Καταχώρηση κτιρίου κάτω από',
        buildingNameBlock: 'Όνομα Κτιρίου / Μπλοκ',
        buildingCode: 'Μοναδικός Κωδικός Κτιρίου',
        buildingCodeDesc: 'Αυτός ο κωδικός χρησιμοποιείται για την εγγραφή και την παρακολούθηση χρηστών.',
        saveChanges: 'Αποθήκευση Αλλαγών',
        saving: 'Αποθήκευση...',
        updateBuilding: 'Ενημέρωση Κτιρίου',
        updateProperty: 'Ενημέρωση Ακινήτου',
        createProperty: 'Δημιουργία Ακινήτου',
        deletePropertyConfirm: 'Είστε σίγουροι; Αυτό θα διαγράψει όλα τα κτίρια που σχετίζονται με αυτό το ακίνητο.',
        deleteBuildingConfirm: 'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το κτίριο;',
        loadFailed: 'Αποτυχία φόρτωσης δεδομένων ακινήτου',
        nameAddressRequired: 'Το όνομα και η διεύθυνση είναι υποχρεωτικά',
        nameCodeRequired: 'Το όνομα και ο κωδικός κτιρίου είναι υποχρεωτικά',
        propertyUpdated: 'Το ακίνητο ενημερώθηκε επιτυχώς',
        propertyCreated: 'Το ακίνητο δημιουργήθηκε επιτυχώς',
        buildingUpdated: 'Το κτίριο ενημερώθηκε επιτυχώς',
        buildingAdded: 'Το κτίριο προστέθηκε επιτυχώς',
        propertyDeleted: 'Το ακίνητο διαγράφηκε',
        buildingDeleted: 'Το κτίριο διαγράφηκε',
        deleteFailed: 'Η διαγραφή απέτυχε'
      },

      // Notifications
      notifications: {
        title: 'Ειδοποιήσεις',
        unreadCount: '{{count}} μη αναγνωσμένες',
        markAllRead: 'Σήμανση όλων ως αναγνωσμένων',
        all: 'Όλες',
        unread: 'Μη αναγνωσμένες',
        loading: 'Φόρτωση ειδοποιήσεων...',
        noUnread: 'Δεν υπάρχουν μη αναγνωσμένες ειδοποιήσεις',
        noNotifications: 'Δεν υπάρχουν ειδοποιήσεις ακόμη',
        loadFailed: 'Αποτυχία φόρτωσης ειδοποιήσεων',
        markReadSuccess: 'Όλες οι ειδοποιήσεις σημειώθηκαν ως αναγνωσμένες',
        markReadFailed: 'Αποτυχία σήμανσης ειδοποιήσεων ως αναγνωσμένων',
        processFailed: 'Αποτυχία επεξεργασίας ειδοποίησης'
      },

      // Email Content
      email: {
        viewDetails: 'Προβολή Λεπτομερειών',
        footer: 'Λαμβάνετε αυτό το μήνυμα επειδή οι ειδοποιήσεις μέσω email είναι ενεργοποιημένες στις ρυθμίσεις του ManageHub.',
        automatedMessage: 'Αυτό είναι ένα αυτοματοποιημένο μήνυμα από το Σύστημα Διαχείρισης Ακινήτων ManageHub.',
        signIn: 'Σύνδεση στο ManageHub',
        passwordReset: {
          title: 'Αίτημα Επαναφοράς Κωδικού',
          greeting: 'Γεια σας {{name}},',
          body: 'Λάβαμε ένα αίτημα για επαναφορά του κωδικού πρόσβασης για τον λογαριασμό σας στο ManageHub. Εάν δεν κάνατε εσείς αυτό το αίτημα, μπορείτε να αγνοήσετε με ασφάλεια αυτό το email.',
          securityNote: 'Σημείωση Ασφαλείας:',
          securityBody: 'Για την ασφάλειά σας, επικοινωνήστε απευθείας με τον διαχειριστή του ακινήτου σας για να λάβετε τον προσωρινό κωδικό πρόσβασής σας ή για να γίνει μη αυτόματη επαναφορά των διαπιστευτηρίων σας.',
          instruction: 'Μόλις έχετε τα νέα σας διαπιστευτήρια, μπορείτε να συνδεθείτε στη διεύθυνση:'
        },
        invitation: {
          subject: 'Καλώς ήρθατε στο ManageHub - Στοιχεία Λογαριασμού',
          greeting: 'Γεια σας {{name}},',
          body: 'Δημιουργήθηκε ένας λογαριασμός για εσάς στο Σύστημα Διαχείρισης Ακινήτων ManageHub.',
          credentialsTitle: 'Τα Στοιχεία Σύνδεσής σας:',
          emailLabel: 'Email:',
          passwordLabel: 'Προσωρινός Κωδικός:',
          urgencyNote: 'Για λόγους ασφαλείας, σας συνιστούμε θερμά να αλλάξετε τον κωδικό πρόσβασής σας αμέσως μετά την πρώτη σας σύνδεση.',
          actionButton: 'Συνδεθείτε στον Λογαριασμό σας',
          securityTip: 'Εάν δεν περιμένατε αυτό το email, επικοινωνήστε με τον διαχειριστή του ακινήτου σας.'
        },
        types: {
          mention: {
            title: 'Ο/Η {{name}} σάς ανέφερε',
            message: 'Σας ανέφεραν στο "{{title}}"',
            preview: 'Σας ανέφεραν: "{{preview}}"'
          },
          faultStatus: {
            title: 'Ενημέρωση Κατάστασης Βλάβης',
            message: 'Η αναφορά σας "{{title}}" άλλαξε από {{oldStatus}} σε {{newStatus}}'
          },
          faultComment: {
            title: 'Νέο Σχόλιο στην Αναφορά Βλάβης σας',
            message: 'Ο/Η {{name}} σχολίασε στο "{{title}}"'
          },
          faultNew: {
            title: 'Νέα Αναφορά Βλάβης',
            message: 'Ο/Η {{name}} ανέφερε: {{title}}'
          },
          topicReply: {
            title: 'Νέα Απάντηση στο Θέμα σας',
            message: 'Ο/Η {{name}} απάντησε στο "{{title}}"'
          },
          announcement: {
            title: 'Νέα Ανακοίνωση',
            message: '{{title}}'
          }
        }
      }
    }
  }
};

// Initialize i18next
i18n.
use(initReactI18next).
init({
  resources,
  lng: localStorage.getItem('language') || 'en',
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false // React already escapes values
  },
  react: {
    useSuspense: false
  }
});

// Listen for language changes and save to localStorage
i18n.on('languageChanged', (lng) => {
  localStorage.setItem('language', lng);
});

// Make i18next available globally for utility functions
if (typeof window !== 'undefined') {
  window.i18next = i18n;
}

export default i18n;